import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, TrendingUp, Home, Ruler, MousePointerClick, Printer, ArrowRight } from "lucide-react";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
    PieChart, Pie, Cell, LineChart, Line, Legend,
} from "recharts";
import api from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import StatusBadge, { STATUS_COLORS } from "@/components/StatusBadge";
import { useAppShell } from "@/components/AppShell";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { tr } from "@/lib/i18n";

const CATEGORY_COLORS = ["#00B4D8", "#FFB703", "#06D6A0", "#EF476F", "#0A2540", "#94A3B8"];

const NARRATION = {
    timeline: "Tracks how many applications entered the system each month. A rising line signals accelerating development interest; dips often follow rate changes or seasonal cycles.",
    category: "Shows the mix of proposals by land-use category. A residential-heavy mix pressures housing supply targets; growth in employment categories supports the local tax base.",
    ward: "Compares application counts (teal bars) against proposed housing units (gold bars) per ward. Wards where units outpace applications are seeing larger, higher-density proposals.",
    status: "A live funnel of the pipeline. A healthy pipeline keeps most files in Submitted/In Review; a build-up in On Hold or long-dwell files signals process bottlenecks.",
};

function Narration({ children, testid }) {
    return (
        <p data-testid={testid} className="text-xs text-slate-400 dark:text-slate-500 leading-relaxed mt-1 mb-4 max-w-xl">
            {children}
        </p>
    );
}

function PerformancePanel() {
    const { tr } = useLanguage();
    const [perf, setPerf] = useState(null);
    useEffect(() => { api.get("/analytics/performance").then((r) => setPerf(r.data)).catch(() => {}); }, []);
    if (!perf) return <div className="h-64 rounded-[18px] bg-white/60 dark:bg-white/5 animate-pulse" data-testid="performance-loading" />;
    const maxAvg = Math.max(...perf.stages.map((s) => s.avg_days), 1);
    return (
        <div className="space-y-6" data-testid="performance-panel">
            <div className="grid grid-cols-3 gap-4">
                {[
                    ["Files measured", perf.overall.files, "#0A2540", "perf-files"],
                    ["On-time vs legislated targets", `${perf.overall.on_time_pct}%`, perf.overall.on_time_pct >= 80 ? "#05A37E" : "#B8860B", "perf-ontime"],
                    ["Files past target today", perf.overall.overdue_count, perf.overall.overdue_count ? "#EF476F" : "#05A37E", "perf-overdue"],
                ].map(([l, v, c, tid]) => (
                    <div key={tid} data-testid={tid} className="bg-card rounded-[18px] border border-white/70 dark:border-white/10 shadow-[0_10px_30px_-14px_rgba(10,37,64,0.18)] p-6 text-center">
                        <div className="font-data font-bold text-3xl" style={{ color: c }}>{v}</div>
                        <div className="text-[11px] uppercase tracking-[0.12em] font-bold text-slate-400 mt-1.5">{tr(l)}</div>
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-card rounded-[18px] border border-white/70 dark:border-white/10 shadow-[0_10px_30px_-14px_rgba(10,37,64,0.18)] p-6">
                    <h3 className="font-heading font-bold text-navy dark:text-white">{tr("Average days per stage")}</h3>
                    <p className="text-[11px] text-slate-400 mt-1 mb-4">{tr("Stage-by-stage processing time across completed milestones.")}</p>
                    <div className="space-y-3">
                        {perf.stages.map((s) => (
                            <div key={s.stage} data-testid={`perf-stage-${s.stage.toLowerCase().replace(/\s+/g, "-")}`}>
                                <div className="flex justify-between text-xs font-semibold text-slate-500 mb-1"><span>{tr(s.stage)}</span><span className="font-data">{s.avg_days}d · {s.files} {tr("files")}</span></div>
                                <div className="h-2 rounded-full bg-slate-100 dark:bg-white/10 overflow-hidden">
                                    <div className="h-full rounded-full bg-gradient-to-r from-[#00B4D8] to-[#2DD4BF]" style={{ width: `${(s.avg_days / maxAvg) * 100}%` }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="bg-card rounded-[18px] border border-white/70 dark:border-white/10 shadow-[0_10px_30px_-14px_rgba(10,37,64,0.18)] p-6">
                    <h3 className="font-heading font-bold text-navy dark:text-white">{tr("On-time % vs legislated timeline")}</h3>
                    <p className="text-[11px] text-slate-400 mt-1 mb-4">{tr("Planning Act decision targets by application type.")}</p>
                    <div className="space-y-2.5">
                        {perf.types.map((t) => (
                            <div key={t.type} className="flex items-center gap-3" data-testid={`perf-type-${t.type.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 w-44 truncate">{tr(t.type)}</span>
                                <div className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-white/10 overflow-hidden">
                                    <div className={`h-full rounded-full ${t.on_time_pct >= 80 ? "bg-[#06D6A0]" : t.on_time_pct >= 50 ? "bg-[#FFB703]" : "bg-[#EF476F]"}`} style={{ width: `${t.on_time_pct}%` }} />
                                </div>
                                <span className="text-[11px] font-data text-slate-400 w-32 text-right">{t.on_time_pct}% · {tr("avg")} {t.avg_days}d / {t.target}d</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className="bg-card rounded-[18px] border border-white/70 dark:border-white/10 shadow-[0_10px_30px_-14px_rgba(10,37,64,0.18)] p-6">
                <h3 className="font-heading font-bold text-navy dark:text-white">{tr("Files exceeding legislated timelines")}</h3>
                <p className="text-[11px] text-slate-400 mt-1 mb-3">{tr("Active files past their Planning Act decision target — candidates for triage.")}</p>
                <div className="space-y-2" data-testid="perf-overdue-list">
                    {perf.overdue.map((o) => (
                        <div key={o.id} className="flex items-center justify-between gap-3 p-3 rounded-[10px] bg-[#EF476F]/5 border border-[#EF476F]/15 flex-wrap" data-testid={`perf-overdue-${o.file_number}`}>
                            <div>
                                <span className="font-data text-xs font-bold text-[#EF476F]">{o.file_number}</span>
                                <span className="text-sm font-semibold text-navy dark:text-white ml-2.5">{o.title}</span>
                                <span className="text-xs text-slate-400 ml-2">{tr(o.type)} · {o.planner_name}</span>
                            </div>
                            <span className="text-[11px] font-extrabold text-[#EF476F]">{o.elapsed}d {tr("elapsed")} · {o.over_by}d {tr("over the")} {o.target}d {tr("target")}</span>
                        </div>
                    ))}
                    {!perf.overdue.length && <div className="text-sm text-slate-400 py-4">{tr("Every active file is inside its legislated timeline.")}</div>}
                </div>
            </div>
        </div>
    );
}

export default function Analytics() {
    const { tr } = useLanguage();
    const { openAI } = useAppShell();
    const [data, setData] = useState(null);
    const [dive, setDive] = useState(null);
    const [diveData, setDiveData] = useState(null);
    const [diveLoading, setDiveLoading] = useState(false);
    const [housing, setHousing] = useState(null);

    useEffect(() => {
        api.get("/analytics").then((r) => setData(r.data)).catch(() => {});
        api.get("/analytics/housing-targets").then((r) => setHousing(r.data)).catch(() => {});
    }, []);

    const deepDive = async (dimension, value) => {
        setDive({ dimension, value });
        setDiveLoading(true);
        setDiveData(null);
        try {
            const { data: d } = await api.get("/analytics/deepdive", { params: { dimension, value } });
            setDiveData(d);
        } catch {
            setDiveData({ applications: [] });
        } finally {
            setDiveLoading(false);
        }
    };

    if (!data) return <div className="grid grid-cols-2 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-64 rounded-[18px] bg-white/60 dark:bg-white/5 animate-pulse" />)}</div>;

    const cards = [
        { label: tr("Total Proposed Units"), value: data.total_units.toLocaleString(), icon: Home, testid: "an-units", color: "#00B4D8" },
        { label: tr("Total Site Area (ha)"), value: data.total_area, icon: Ruler, testid: "an-area", color: "#06D6A0" },
        { label: tr("Active Files"), value: data.by_status.reduce((n, s) => n + s.value, 0), icon: TrendingUp, testid: "an-files", color: "#FFB703" },
        { label: tr("Wards With Activity"), value: data.by_ward.length, icon: MousePointerClick, testid: "an-wards", color: "#4F5DD5" },
    ];

    return (
        <div data-testid="analytics-page" className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-heading text-navy dark:text-white">{tr("Analytics & Growth")}</h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{tr("Development activity, growth management and workload analysis — click any chart segment to deep-dive")}</p>
                </div>
                <div className="flex gap-2">
                    <button data-testid="analytics-print-btn" onClick={() => window.print()} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] text-sm font-bold border border-slate-300 dark:border-white/15 bg-background hover:border-[#00B4D8] hover:text-[#0092B0] transition-colors">
                        <Printer className="w-4 h-4" /> {tr("Print report")}
                    </button>
                    <button data-testid="ask-the-data-btn" onClick={() => openAI({ type: "data" })} className="brand-btn inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] text-sm font-bold">
                        <Sparkles className="w-4 h-4" /> {tr("Ask the Data")}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {cards.map((k, i) => (
                    <motion.div key={k.label} data-testid={k.testid} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                        className="bg-card rounded-[18px] border border-white/70 dark:border-white/10 border-l-4 p-5 shadow-[0_10px_30px_-14px_rgba(10,37,64,0.18)] hover:-translate-y-0.5 transition-all"
                        style={{ borderLeftColor: k.color }}>
                        <div className="flex items-center justify-between">
                            <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-400">{k.label}</div>
                            <div className="w-8 h-8 rounded-[10px] flex items-center justify-center" style={{ backgroundColor: `${k.color}1A` }}>
                                <k.icon className="w-4 h-4" style={{ color: k.color }} />
                            </div>
                        </div>
                        <div className="mt-2 font-data font-bold text-[26px] text-navy dark:text-white">{k.value}</div>
                    </motion.div>
                ))}
            </div>

            <Tabs defaultValue="activity">
                <TabsList className="bg-white/70 dark:bg-white/5 border border-white/60 dark:border-white/10 p-1.5 rounded-[14px]">
                    <TabsTrigger value="activity" data-testid="analytics-tab-activity" className="text-xs rounded-[10px] px-4 py-2 data-[state=active]:!bg-[#0A2540] data-[state=active]:!text-white">{tr("Development Activity")}</TabsTrigger>
                    <TabsTrigger value="growth" data-testid="analytics-tab-growth" className="text-xs rounded-[10px] px-4 py-2 data-[state=active]:!bg-[#0A2540] data-[state=active]:!text-white">{tr("Growth Management")}</TabsTrigger>
                    <TabsTrigger value="workload" data-testid="analytics-tab-workload" className="text-xs rounded-[10px] px-4 py-2 data-[state=active]:!bg-[#0A2540] data-[state=active]:!text-white">{tr("Workload")}</TabsTrigger>
                    <TabsTrigger value="housing" data-testid="analytics-tab-housing" className="text-xs rounded-[10px] px-4 py-2 data-[state=active]:!bg-[#0A2540] data-[state=active]:!text-white">{tr("Housing Targets")}</TabsTrigger>
                    <TabsTrigger value="performance" data-testid="analytics-tab-performance" className="text-xs rounded-[10px] px-4 py-2 data-[state=active]:!bg-[#0A2540] data-[state=active]:!text-white">{tr("Performance")}</TabsTrigger>
                </TabsList>

                <TabsContent value="activity" className="mt-6">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        <div className="lg:col-span-7 bg-card rounded-[18px] border border-white/70 dark:border-white/10 shadow-[0_10px_30px_-14px_rgba(10,37,64,0.18)] p-6">
                            <h3 className="font-heading font-bold text-navy dark:text-white">{tr("Submission Timeline")}</h3>
                            <Narration testid="narration-timeline">{tr(NARRATION.timeline)}</Narration>
                            <ResponsiveContainer width="100%" height={260}>
                                <LineChart data={data.timeline}>
                                    <defs>
                                        <linearGradient id="lineGlow" x1="0" y1="0" x2="1" y2="0">
                                            <stop offset="0%" stopColor="#00B4D8" />
                                            <stop offset="100%" stopColor="#2DD4BF" />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94A3B8" />
                                    <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" allowDecimals={false} />
                                    <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 12 }} />
                                    <Line type="monotone" dataKey="applications" stroke="url(#lineGlow)" strokeWidth={3} dot={{ r: 5, fill: "#00B4D8", strokeWidth: 2, stroke: "#fff" }} activeDot={{ r: 7 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="lg:col-span-5 bg-card rounded-[18px] border border-white/70 dark:border-white/10 shadow-[0_10px_30px_-14px_rgba(10,37,64,0.18)] p-6">
                            <h3 className="font-heading font-bold text-navy dark:text-white">{tr("By Category")}</h3>
                            <Narration testid="narration-category">{tr(NARRATION.category)}</Narration>
                            <ResponsiveContainer width="100%" height={240}>
                                <PieChart>
                                    <Pie data={data.by_category} dataKey="value" nameKey="name" innerRadius={52} outerRadius={84} paddingAngle={3}
                                        onClick={(d) => d?.name && deepDive("category", d.name)} className="cursor-pointer">
                                        {data.by_category.map((c, i) => <Cell key={c.name} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} className="hover:opacity-80 transition-opacity" />)}
                                    </Pie>
                                    <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 12 }} />
                                    <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="growth" className="mt-6">
                    <div className="bg-card rounded-[18px] border border-white/70 dark:border-white/10 shadow-[0_10px_30px_-14px_rgba(10,37,64,0.18)] p-6">
                        <h3 className="font-heading font-bold text-navy dark:text-white">{tr("Units & Applications by Ward")}</h3>
                        <Narration testid="narration-ward">{tr(NARRATION.ward)}</Narration>
                        <ResponsiveContainer width="100%" height={320}>
                            <BarChart data={data.by_ward}>
                                <defs>
                                    <linearGradient id="barTeal" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#2DD4BF" />
                                        <stop offset="100%" stopColor="#0092B0" />
                                    </linearGradient>
                                    <linearGradient id="barGold" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#FFD166" />
                                        <stop offset="100%" stopColor="#E69F00" />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94A3B8" />
                                <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke="#94A3B8" allowDecimals={false} />
                                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke="#94A3B8" />
                                <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 12 }} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar yAxisId="left" dataKey="applications" name="Applications" fill="url(#barTeal)" radius={[6, 6, 0, 0]}
                                    onClick={(d) => d?.name && deepDive("ward", d.name)} className="cursor-pointer" />
                                <Bar yAxisId="right" dataKey="units" name="Proposed Units" fill="url(#barGold)" radius={[6, 6, 0, 0]}
                                    onClick={(d) => d?.name && deepDive("ward", d.name)} className="cursor-pointer" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </TabsContent>

                <TabsContent value="workload" className="mt-6">
                    <div className="bg-card rounded-[18px] border border-white/70 dark:border-white/10 shadow-[0_10px_30px_-14px_rgba(10,37,64,0.18)] p-6">
                        <h3 className="font-heading font-bold text-navy dark:text-white">{tr("Files by Status")}</h3>
                        <Narration testid="narration-status">{tr(NARRATION.status)}</Narration>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={data.by_status} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                                <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94A3B8" allowDecimals={false} />
                                <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} stroke="#94A3B8" width={90} />
                                <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 12 }} />
                                <Bar dataKey="value" radius={[0, 6, 6, 0]} onClick={(d) => d?.name && deepDive("status", d.name)} className="cursor-pointer">
                                    {data.by_status.map((s) => <Cell key={s.name} fill={STATUS_COLORS[s.name] || "#94A3B8"} className="hover:opacity-80 transition-opacity" />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </TabsContent>
                <TabsContent value="housing" className="mt-6">
                    <div className="bg-card rounded-[18px] border border-white/70 dark:border-white/10 p-6" data-testid="housing-targets">
                        <h3 className="font-heading font-bold text-navy dark:text-white">{tr("Housing Targets")}{housing?.year ? ` · ${housing.year}` : ""}</h3>
                        <p className="text-xs text-slate-400 mt-1 mb-5">{tr("Ward-level unit targets against the live pipeline — in-progress plus approved units as a share of the annual target. 60%+ is treated as on track.")}</p>
                        <div className="space-y-3">
                            {(housing?.targets || []).map((t) => (
                                <div key={t.ward} className="grid grid-cols-12 items-center gap-3" data-testid={`housing-${t.ward.replace(/\s/g, "-")}`}>
                                    <div className="col-span-2 text-sm font-bold">{t.ward}</div>
                                    <div className="col-span-7 h-3 rounded-full bg-slate-100 dark:bg-white/5 overflow-hidden flex">
                                        <div className="h-full bg-[#00B4D8]" style={{ width: `${Math.min(100, (100 * t.in_progress_units) / t.target_units)}%` }} />
                                        <div className="h-full bg-[#06D6A0]" style={{ width: `${Math.min(100, (100 * t.approved_units) / t.target_units)}%` }} />
                                    </div>
                                    <div className="col-span-2 text-xs font-data text-slate-500 dark:text-slate-300">{t.in_progress_units + t.approved_units} / {t.target_units} ({t.pipeline_pct}%)</div>
                                    <div className="col-span-1 text-right">
                                        <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${t.on_track ? "bg-[#06D6A0]/10 text-[#05A37E]" : "bg-[#EF476F]/10 text-[#EF476F]"}`}>{t.on_track ? tr("On track") : tr("Off track")}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-4 mt-4 text-[10px] font-bold text-slate-400">
                            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#00B4D8]" />{tr("In progress")}</span>
                            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#06D6A0]" />{tr("Approved")}</span>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="performance" className="mt-6">
                    <PerformancePanel />
                </TabsContent>
            </Tabs>

            <Dialog open={!!dive} onOpenChange={(o) => !o && setDive(null)}>
                <DialogContent className="sm:max-w-2xl rounded-[16px]" data-testid="deepdive-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">
                            Deep dive — {dive?.dimension} · <span className="text-teal">{dive?.value}</span>
                        </DialogTitle>
                    </DialogHeader>
                    <div className="mt-2 max-h-[420px] overflow-y-auto">
                        {diveLoading && <div className="py-12 text-center text-sm text-slate-400">{tr("Loading files…")}</div>}
                        {diveData && !diveLoading && (
                            <>
                                <div className="text-xs text-slate-400 mb-3">{diveData.count} application{diveData.count !== 1 ? "s" : ""} match this segment</div>
                                <div className="space-y-2">
                                    {diveData.applications.map((a) => (
                                        <Link key={a.id} to={`/applications/${a.id}`} data-testid={`deepdive-${a.file_number}`}
                                            className="flex items-center justify-between gap-3 p-3.5 rounded-[10px] border border-slate-100 dark:border-white/10 hover:border-[#00B4D8] hover:bg-[#00B4D8]/5 transition-colors">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-data text-xs text-teal font-bold">{a.file_number}</span>
                                                    <span className="text-[11px] text-slate-400">{a.type}</span>
                                                </div>
                                                <div className="text-sm font-semibold text-navy dark:text-slate-100 truncate mt-0.5">{a.title}</div>
                                            </div>
                                            <div className="flex items-center gap-2.5 shrink-0">
                                                <span className="font-data text-xs text-slate-400">{a.statistics?.units || 0} units</span>
                                                <StatusBadge status={a.status} />
                                                <ArrowRight className="w-3.5 h-3.5 text-slate-300" />
                                            </div>
                                        </Link>
                                    ))}
                                    {diveData.applications.length === 0 && <div className="py-10 text-center text-sm text-slate-400">{tr("No applications in this segment.")}</div>}
                                </div>
                            </>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
